import { Button } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';

type PrimitivePoint = {
  x: string;
  y: number | number[] | null;
};

export type SalesChartSeries = {
  name: string;
  type?: string;
  data: PrimitivePoint[];
};

const formatNumber = (value: number | null | undefined, currency: string) => {
  if (value == null || Number.isNaN(value)) return '-';
  return `${value.toFixed(2)} ${currency}`;
};

const getPointValue = (series: SalesChartSeries, category: string) =>
  series.data.find((point) => point.x === category)?.y ?? null;

const buildRows = (
  categories: string[],
  series: SalesChartSeries[],
  currency: string
) => {
  const rows: string[][] = [];

  series.forEach((serie) => {
    if (serie.type === 'boxPlot') {
      const stats = [
        { label: 'Min', index: 0 },
        { label: 'Q1', index: 1 },
        { label: 'Mediana', index: 2 },
        { label: 'Q3', index: 3 },
        { label: 'Max', index: 4 },
      ];

      stats.forEach((stat) => {
        rows.push([
          serie.name,
          stat.label,
          ...categories.map((category) => {
            const value = getPointValue(serie, category);
            return Array.isArray(value)
              ? formatNumber(value[stat.index], currency)
              : '-';
          }),
        ]);
      });
      return;
    }

    rows.push([
      serie.name,
      'Precio',
      ...categories.map((category) => {
        const value = getPointValue(serie, category);
        return typeof value === 'number' ? formatNumber(value, currency) : '-';
      }),
    ]);
  });

  return rows;
};

export const exportSalesChartToExcel = (
  fileName: string,
  categories: string[],
  series: SalesChartSeries[],
  currency: string
) => {
  const rows = [
    ['Serie', 'Estadística', ...categories],
    ...buildRows(categories, series, currency),
  ];
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  worksheet['!cols'] = [
    { wch: 28 },
    { wch: 14 },
    ...categories.map(() => ({ wch: 16 })),
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  XLSX.writeFile(workbook, fileName);
};

interface SalesChartTableProps {
  categories: string[];
  series: SalesChartSeries[];
  currency: string;
  emptyLabel: string;
}

export const SalesChartTable = ({
  categories,
  series,
  currency,
  emptyLabel,
}: SalesChartTableProps) => {
  if (!categories.length || !series.length) {
    return (
      <div className='flex items-center justify-center h-64'>
        <p className='text-default-400'>{emptyLabel}</p>
      </div>
    );
  }

  const rows = buildRows(categories, series, currency);

  return (
    <div className='overflow-x-auto'>
      <table className='min-w-full border-collapse'>
        <thead>
          <tr className='bg-default-100'>
            <th className='border px-4 py-2 text-left'>Serie</th>
            <th className='border px-4 py-2 text-left'>Estadística</th>
            {categories.map((category) => (
              <th key={category} className='border px-4 py-2 text-center'>
                {category}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row[0]}-${row[1]}-${index}`}>
              {row.map((cell, cellIndex) => (
                <td
                  key={`${row[0]}-${row[1]}-${cellIndex}`}
                  className={`border px-4 py-2 ${
                    cellIndex < 2 ? 'font-medium' : 'text-center'
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface SalesExcelButtonProps {
  categories: string[];
  series: SalesChartSeries[];
  currency: string;
  fileName: string;
  label: string;
}

export const SalesExcelButton = ({
  categories,
  series,
  currency,
  fileName,
  label,
}: SalesExcelButtonProps) => (
  <Button
    size='sm'
    variant='flat'
    startContent={<Icon icon='tabler:download' />}
    isDisabled={!categories.length || !series.length}
    onClick={() =>
      exportSalesChartToExcel(fileName, categories, series, currency)
    }
  >
    {label}
  </Button>
);
